// Note: WorkOS tokens are stored in HttpOnly cookies and cannot be read from JavaScript.
// Use fetchWorkosTokenStatus() to get token information via the router's /auth/session endpoint.

const workosApiBaseUrl =
  import.meta.env.VITE_WORKOS_API_BASE_URL?.replace(/\/$/, '') ?? 'https://api.workos.com'

const routerApiBaseUrl =
  import.meta.env.VITE_ROUTER_API_URL?.replace(/\/$/, '') ?? 'https://router.nobgp.com'

type WorkosSessionRequestOptions = {
  method: 'GET' | 'POST' | 'DELETE'
  query?: string
  body?: Record<string, unknown>
  contentType?: string
}

const AUTH_SESSION_PATH = '/auth/session'

const buildSessionUrl = (query: string = '') =>
  `${routerApiBaseUrl}${AUTH_SESSION_PATH}${query}`

const requestWorkosSession = async (
  options: WorkosSessionRequestOptions
): Promise<Response | null> => {
  const url = buildSessionUrl(options.query ?? '')
  const body = options.body ? JSON.stringify(options.body) : undefined
  const contentType = options.contentType ?? (body ? 'application/json' : undefined)

  const response = await fetch(url, {
    method: options.method,
    credentials: 'include',
    ...(contentType ? { headers: { 'content-type': contentType } } : {}),
    ...(body ? { body } : {}),
  })

  if (!response.ok) {
    return null
  }

  return response
}

const requestWorkosSessionJson = async <T>(
  options: WorkosSessionRequestOptions,
  logContext: string
): Promise<T | null> => {
  try {
    const response = await requestWorkosSession(options)
    if (!response) {
      return null
    }
    return (await response.json()) as T
  } catch (error) {
    console.warn(`workos: ${logContext}`, error)
    return null
  }
}

export const decodeWorkosAccessTokenPayload = (token: string): Record<string, unknown> | null => {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=')
    return JSON.parse(atob(padded)) as Record<string, unknown>
  } catch {
    return null
  }
}

export interface WorkosTokenStatus {
  expiresAt: number | null
  hasAccessToken: boolean
  hasRefreshToken: boolean
}

export interface WorkosAccessTokenStatus {
  accessToken: string | null
  expiresAt: number | null
  hasRefreshToken: boolean
}

export const fetchWorkosTokenStatus = async (): Promise<WorkosTokenStatus | null> => {
  const data = await requestWorkosSessionJson<{
    expiresAt?: number | null
    hasAccessToken?: boolean
    hasRefreshToken?: boolean
  }>({ method: 'GET' }, 'failed to fetch token status')

  if (!data) {
    return null
  }

  return {
    expiresAt: data.expiresAt ?? null,
    hasAccessToken: data.hasAccessToken ?? false,
    hasRefreshToken: data.hasRefreshToken ?? false,
  }
}

export const clearWorkosSessionCookies = async (sessionId?: string | null) => {
  try {
    await requestWorkosSession({
      method: 'DELETE',
      body: sessionId && sessionId.length > 0 ? { sessionId } : {},
    })
  } catch (error) {
    console.warn('workos: failed to clear session cookies', error)
  }
}

export const buildWorkosLogoutUrl = (sessionId: string, returnTo?: string) => {
  const url = new URL('/user_management/sessions/logout', workosApiBaseUrl)
  url.searchParams.set('session_id', sessionId)

  if (returnTo) {
    url.searchParams.set('return_to', returnTo)
  }

  return url.toString()
}

const fetchWorkosAccessToken = async (): Promise<WorkosAccessTokenStatus | null> => {
  const data = await requestWorkosSessionJson<{
    accessToken?: string
    expiresAt?: number | null
    hasRefreshToken?: boolean
  }>({ method: 'GET', query: '?return_token=true' }, 'failed to fetch access token')

  if (!data) {
    return null
  }

  return {
    accessToken: data.accessToken ?? null,
    expiresAt: data.expiresAt ?? null,
    hasRefreshToken: data.hasRefreshToken ?? false,
  }
}

const refreshAndFetchAccessToken = async (): Promise<WorkosAccessTokenStatus | null> => {
  const refreshResult = await refreshWorkosToken()
  if (!refreshResult.success || !refreshResult.accessToken || typeof refreshResult.expiresIn !== 'number') {
    await clearCookiesOnInvalidRefreshToken(refreshResult.error)
    return null
  }

  // Use token data directly from POST response
  const now = Date.now()
  const expiresAt = now + refreshResult.expiresIn * 1000

  return {
    accessToken: refreshResult.accessToken,
    expiresAt,
    hasRefreshToken: true,
  }
}

/**
 * Initialize token metadata from cookies on page load
 * Calls router which reads workos-access-token cookie and returns its value
 * Also validates that the token is not expired
 *
 * @param attemptRefresh - If true (default), attempt to refresh an expired token using the refresh token cookie
 */
export const initWorkosToken = async (attemptRefresh: boolean = true): Promise<WorkosAccessTokenStatus | null> => {
  const tokenStatus = await fetchWorkosAccessToken()
  if (!tokenStatus) {
    return null
  }

  if (!tokenStatus.accessToken) {
    if (attemptRefresh && tokenStatus.hasRefreshToken) {
      return await refreshAndFetchAccessToken()
    }
    return null
  }

  if (tokenStatus.expiresAt) {
    const now = Date.now()
    if (now >= tokenStatus.expiresAt) {
      if (attemptRefresh) {
        return await refreshAndFetchAccessToken()
      }
      return null
    }
  }

  return tokenStatus
}

const USER_ID_COOKIE_NAME = 'nobgp-user-id'

/**
 * Get the user ID from cookie (set during auth callback by the router)
 * This cookie is non-HttpOnly so it can be read from JavaScript
 */
export const getUserId = (): string | null => {
  const cookies = document.cookie.split(';')
  for (const cookie of cookies) {
    const [name, ...valueParts] = cookie.split('=')
    if (name?.trim() === USER_ID_COOKIE_NAME) {
      const value = valueParts.join('=').trim()
      try {
        return decodeURIComponent(value) || null
      } catch {
        return value || null
      }
    }
  }
  return null
}

/**
 * Clear session cookies if refresh error indicates the refresh token is definitively invalid.
 * Does not clear cookies for transient errors (network/5xx) to allow recovery.
 */
export const clearCookiesOnInvalidRefreshToken = async (error?: string): Promise<void> => {
  const isPermanentFailure = error === 'invalid_refresh_token' || error === 'missing_refresh_token'
  if (isPermanentFailure) {
    await clearWorkosSessionCookies()
  }
}

export const refreshWorkosToken = async (): Promise<{
  success: boolean
  expiresIn?: number
  accessToken?: string
  error?: string
}> => {
  try {
    const url = buildSessionUrl('')
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
    })

    // Handle non-ok responses - distinguish between permanent and transient failures
    if (!response.ok) {
      try {
        const errorData = (await response.json()) as { error?: string }
        if (errorData.error) {
          return { success: false, error: errorData.error }
        }
      } catch {
        // Failed to parse JSON, fall through to status-based error
      }

      if (response.status >= 400 && response.status < 500) {
        return { success: false, error: 'invalid_refresh_token' }
      }
      return { success: false, error: 'server_error' }
    }

    const data = (await response.json()) as {
      success: boolean
      expiresIn?: number
      accessToken?: string
      error?: string
    }

    return data
  } catch (error) {
    console.warn('workos: failed to refresh token', error)
    return { success: false, error: 'network_error' }
  }
}
